/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'
import type { TeamFinderRow } from '@sportninja/common/features/teamFinder/resultRow'
import DataGrid from 'src/components/ts-components/DataGrid'
import { useResultRows } from '../hooks/useResultRows'
import { useResultColumns } from '../hooks/useResultColumns'

interface Props {
  results: CompetitionResult[]
}

/** Roomy enough for a 28px team logo to sit in without crowding the text. */
const ROW_HEIGHT = 48
const HEADER_HEIGHT = 44

/**
 * The results as a table — every value the card shows, one per column.
 *
 * `domLayout='autoHeight'` rather than a fixed height: the page pages results
 * in below the grid via "Show More", so the grid must grow with its rows
 * instead of scrolling inside a fixed box that would trap the footer beneath
 * it. Page sizes here are small (one API page at a time), so materialising
 * every row costs little.
 */
const TeamFinderTable = ({ results }: Props) => {
  const rows = useResultRows(results)
  const columnDefs = useResultColumns()

  return (
    <div css={wrapStyle}>
      <DataGrid<TeamFinderRow>
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        domLayout='autoHeight'
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        data-testid='team-finder-table'
      />
    </div>
  )
}

export default TeamFinderTable

// `min-width: 0` lets the grid shrink inside the results flex column and scroll
// its own columns, rather than pushing the page sideways.
const wrapStyle = css`
  width: 100%;
  min-width: 0;
`
